import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Loader2, Package, FileText, Users, MessageSquare, CheckCircle2, DollarSign, Stethoscope, Truck, TrendingUp, Wallet, AlertTriangle } from "lucide-react";

const STAT_CARDS = [
  { key: "total_orders", label: "Total Orders", icon: Package, accent: "text-paw-accent" },
  { key: "pending_orders", label: "Pending Orders", icon: Loader2, accent: "text-amber-700 dark:text-amber-400" },
  { key: "completed_orders", label: "Completed", icon: CheckCircle2, accent: "text-emerald-700 dark:text-emerald-400" },
  { key: "total_estimates", label: "Estimates", icon: FileText, accent: "text-sky-700 dark:text-sky-400" },
  { key: "new_estimates", label: "New Estimates", icon: FileText, accent: "text-yellow-400" },
  { key: "total_customers", label: "Customers", icon: Users, accent: "text-violet-700 dark:text-violet-400" },
  { key: "total_vets", label: "Vets", icon: Users, accent: "text-pink-600 dark:text-pink-400" },
  { key: "new_chat_contacts", label: "Chat Leads", icon: MessageSquare, accent: "text-cyan-700 dark:text-cyan-400" },
];

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [fin, setFin] = useState(null);
  const [period, setPeriod] = useState("all");
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/admin/dashboard").then((s) => setStats(s.data)).catch((e) => setError(formatApiError(e)));
  }, []);

  useEffect(() => {
    setFin(null);
    api.get(`/admin/financials?period=${period}`).then((f) => setFin(f.data)).catch((e) => setError(formatApiError(e)));
  }, [period]);

  return (
    <div className="px-3 sm:px-6 lg:px-10 py-5 sm:py-8 lg:py-10 max-w-7xl space-y-8 sm:space-y-10" data-testid="admin-dashboard">
      <div>
        <p className="font-manrope text-[11px] uppercase tracking-widest text-paw-text-muted dark:text-white/40 mb-1.5">Overview</p>
        <h1 className="font-outfit font-bold text-2xl sm:text-3xl tracking-tight">Dashboard</h1>
      </div>
      {error && <p className="text-red-700 dark:text-red-400 text-sm font-manrope">{error}</p>}

      {/* FINANCIALS */}
      <section data-testid="financials-section">
        <div className="flex items-center gap-2 mb-3 sm:mb-4 flex-wrap">
          <TrendingUp className="w-4 h-4 text-emerald-700 dark:text-emerald-400" />
          <h2 className="font-outfit font-semibold text-base sm:text-lg">Financials</h2>
          <span className="ml-1 font-manrope text-[11px] sm:text-xs text-paw-text-muted dark:text-white/40">{fin ? `${fin.paid_orders} paid order${fin.paid_orders === 1 ? "" : "s"}` : ""}</span>
          <div className="ml-auto flex gap-1.5">
            {[["month", "This month"], ["quarter", "Quarter"], ["year", "Year"], ["all", "All time"]].map(([k, l]) => (
              <button key={k} onClick={() => setPeriod(k)} data-testid={`fin-period-${k}`}
                className={`px-3 h-8 rounded-lg font-manrope text-[11px] sm:text-xs transition-colors ${period === k ? "bg-paw-accent text-white" : "bg-black/[0.03] dark:bg-white/5 text-paw-text-secondary dark:text-white/60 hover:bg-black/[0.06] dark:hover:bg-white/10"}`}>
                {l}
              </button>
            ))}
          </div>
        </div>

        {!fin ? (
          <div className="flex items-center gap-2 text-paw-text-muted dark:text-white/40 font-manrope text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : (
          <>
            {/* the money, in the order it actually moves */}
            <div className="flex flex-col lg:flex-row lg:items-stretch gap-2 lg:gap-0" data-testid="fin-flow">
              {[
                { key: "revenue", label: "Customer pays", value: fin.revenue, note: "Revenue collected", tone: "emerald", icon: DollarSign },
                { op: "−", key: "med_cost", label: "Pharmacy cost", value: fin.med_cost, note: "What the medication costs us", tone: "plain", icon: Package },
                { op: "−", key: "delivery_paid", label: "Delivery out", value: fin.delivery_paid, note: "Paid to the fulfillment centre", tone: "plain", icon: Truck },
                { op: "−", key: "vet_fees", label: "Vet fees", value: fin.vet_fees, note: "Signature fees owed", tone: "plain", icon: Stethoscope },
                { op: "=", key: "net_profit", label: "You keep", value: fin.net_profit, note: `${fin.margin_pct}% margin`, tone: fin.net_profit >= 0 ? "profit" : "loss", icon: Wallet },
              ].map((c) => (
                <div key={c.key} className="flex items-stretch flex-1 min-w-0">
                  {c.op && (
                    <div className="hidden lg:flex items-center justify-center w-8 shrink-0 font-outfit font-bold text-lg text-paw-text-muted dark:text-white/25">{c.op}</div>
                  )}
                  <div data-testid={`fin-${c.key}`} className={`flex-1 min-w-0 rounded-xl sm:rounded-2xl p-3.5 sm:p-5 border ${
                    c.tone === "emerald" ? "bg-emerald-500/10 border-emerald-500/20"
                    : c.tone === "profit" ? "bg-emerald-500/15 border-emerald-500/30"
                    : c.tone === "loss" ? "bg-red-500/10 border-red-500/25"
                    : "bg-black/[0.02] dark:bg-white/[0.03] border-paw-border dark:border-white/5"}`}>
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-manrope text-[10px] sm:text-[11px] uppercase tracking-wider text-paw-text-muted dark:text-white/40">{c.label}</p>
                      <c.icon className={`w-4 h-4 shrink-0 ${c.tone === "loss" ? "text-red-600 dark:text-red-400" : c.tone === "plain" ? "text-paw-text-muted dark:text-white/30" : "text-emerald-700 dark:text-emerald-400"}`} />
                    </div>
                    <p className={`font-outfit font-bold text-xl sm:text-2xl tracking-tight mt-1.5 ${
                      c.tone === "loss" ? "text-red-600 dark:text-[#FCA5A5]"
                      : c.tone === "plain" ? "" : "text-emerald-700 dark:text-emerald-200"}`}>
                      {c.tone === "plain" && c.value > 0 ? `−${money(c.value).slice(1)}` : money(c.value)}
                    </p>
                    <p className="font-manrope text-[10px] sm:text-[11px] text-paw-text-muted dark:text-white/35 mt-1">{c.note}</p>
                  </div>
                </div>
              ))}
            </div>

            {fin.cost_unknown_orders > 0 && (
              <div className="mt-3 rounded-xl border border-amber-500/25 bg-amber-500/10 p-3.5 flex items-start gap-2.5" data-testid="fin-cost-warning">
                <AlertTriangle className="w-4 h-4 text-amber-700 dark:text-amber-400 shrink-0 mt-0.5" />
                <p className="font-manrope text-xs text-amber-800 dark:text-amber-200/90 leading-relaxed">
                  <strong>{fin.cost_unknown_orders}</strong> paid order{fin.cost_unknown_orders === 1 ? " has" : "s have"} no medication cost on file, so {fin.cost_unknown_orders === 1 ? "it is" : "they are"} counted at zero cost and profit above is overstated by whatever {fin.cost_unknown_orders === 1 ? "it" : "they"} actually cost. This happens when a medication was quoted by hand instead of from the pricing catalogue. Add it to Pricing Engine with its cost and future orders will be exact.
                </p>
              </div>
            )}

            {/* the things that are not cost of sale */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 sm:gap-3 mt-3">
              <div className="rounded-xl border border-paw-border dark:border-white/5 bg-black/[0.02] dark:bg-white/[0.03] p-3.5" data-testid="fin-delivery-margin">
                <p className="font-manrope text-[10px] uppercase tracking-wider text-paw-text-muted dark:text-white/40">Delivery in vs out</p>
                <p className={`font-outfit font-bold text-lg mt-1.5 ${fin.delivery_margin > 0 ? "text-emerald-700 dark:text-emerald-300" : fin.delivery_margin < 0 ? "text-red-600 dark:text-red-400" : ""}`}>
                  {fin.delivery_margin >= 0 ? "+" : "−"}{money(Math.abs(fin.delivery_margin)).slice(1)}
                </p>
                <p className="font-manrope text-[11px] text-paw-text-muted dark:text-white/35 mt-1">Charged {money(fin.delivery_charged)}, paid out {money(fin.delivery_paid)}</p>
              </div>
              <div className="rounded-xl border border-paw-border dark:border-white/5 bg-black/[0.02] dark:bg-white/[0.03] p-3.5" data-testid="fin-donations">
                <p className="font-manrope text-[10px] uppercase tracking-wider text-paw-text-muted dark:text-white/40">Donations pledged</p>
                <p className="font-outfit font-bold text-lg mt-1.5">{money(fin.donations_accrued)}</p>
                <p className="font-manrope text-[11px] text-paw-text-muted dark:text-white/35 mt-1">Owed to the surgery fund, not yet paid. Not deducted above.</p>
              </div>
              <div className="rounded-xl border border-paw-border dark:border-white/5 bg-black/[0.02] dark:bg-white/[0.03] p-3.5" data-testid="fin-refunds">
                <p className="font-manrope text-[10px] uppercase tracking-wider text-paw-text-muted dark:text-white/40">Refunded</p>
                <p className={`font-outfit font-bold text-lg mt-1.5 ${fin.refunded_amount > 0 ? "text-red-600 dark:text-red-400" : ""}`}>{money(fin.refunded_amount)}</p>
                <p className="font-manrope text-[11px] text-paw-text-muted dark:text-white/35 mt-1">{fin.refunded_orders} order{fin.refunded_orders === 1 ? "" : "s"}, already out of revenue</p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-3">
              <div className="rounded-2xl border border-paw-border dark:border-white/5 bg-white dark:bg-[#1a1d27] p-4 sm:p-5">
                <p className="font-outfit font-semibold text-sm mb-3 flex items-center gap-2"><Stethoscope className="w-4 h-4 text-violet-700 dark:text-violet-400" /> Owed to Vets</p>
                {(fin.by_vet || []).length === 0 ? (
                  <p className="font-manrope text-xs text-paw-text-muted dark:text-white/35">Nothing owed in this period.</p>
                ) : (
                  <table className="w-full">
                    <thead><tr className="text-left font-manrope text-[10px] uppercase tracking-wider text-paw-text-muted dark:text-white/40">
                      <th className="pb-2 font-medium">Vet</th><th className="pb-2 font-medium text-right">#</th><th className="pb-2 font-medium text-right">Fee</th><th className="pb-2 font-medium text-right">Total</th>
                    </tr></thead>
                    <tbody>
                      {fin.by_vet.map((v) => (
                        <tr key={v.vet_id} className="border-t border-paw-border dark:border-white/5">
                          <td className="py-2 font-manrope text-sm">{v.name || "Vet"}<span className="block text-[11px] text-paw-text-muted dark:text-white/35">{v.license_number}</span></td>
                          <td className="py-2 font-manrope text-sm text-right">{v.signed_count}</td>
                          <td className="py-2 font-manrope text-sm text-right">{money(v.signature_fee)}</td>
                          <td className="py-2 font-manrope text-sm text-right font-semibold text-violet-700 dark:text-violet-300">{money(v.total_owed)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="rounded-2xl border border-paw-border dark:border-white/5 bg-white dark:bg-[#1a1d27] p-4 sm:p-5">
                <p className="font-outfit font-semibold text-sm mb-3 flex items-center gap-2"><Truck className="w-4 h-4 text-sky-700 dark:text-sky-400" /> Owed to Fulfillment Centres</p>
                {(fin.by_fc || []).length === 0 ? (
                  <p className="font-manrope text-xs text-paw-text-muted dark:text-white/35">Nothing owed in this period.</p>
                ) : (
                  <table className="w-full">
                    <thead><tr className="text-left font-manrope text-[10px] uppercase tracking-wider text-paw-text-muted dark:text-white/40">
                      <th className="pb-2 font-medium">Centre</th><th className="pb-2 font-medium text-right">Orders</th><th className="pb-2 font-medium text-right">Owed</th>
                    </tr></thead>
                    <tbody>
                      {fin.by_fc.map((f) => (
                        <tr key={f.center_id} className="border-t border-paw-border dark:border-white/5">
                          <td className="py-2 font-manrope text-sm">{f.name}</td>
                          <td className="py-2 font-manrope text-sm text-right">{f.orders}</td>
                          <td className="py-2 font-manrope text-sm text-right font-semibold text-sky-700 dark:text-sky-300">{money(f.shipping_owed)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </>
        )}
      </section>

      {/* GENERAL STATS */}
      <section>
        <h2 className="font-outfit font-semibold text-base sm:text-lg mb-3 sm:mb-4">Pipeline</h2>
        {!stats ? (
          <div className="flex items-center gap-2 text-paw-text-muted dark:text-white/40 font-manrope text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : (
          <>
            {stats.open_alerts > 0 && (
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl sm:rounded-2xl p-3 sm:p-4 flex items-start gap-3 mb-3 sm:mb-4" data-testid="alerts-banner">
                <AlertTriangle className="w-5 h-5 text-amber-700 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-outfit font-semibold text-sm text-amber-800 dark:text-amber-200">{stats.open_alerts} open alert{stats.open_alerts === 1 ? "" : "s"}</p>
                  <p className="font-manrope text-xs text-amber-100/70 mt-0.5">Review them in the Alerts tab. AI failures (e.g. invalid OpenAI key) are flagged here.</p>
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5 sm:gap-4">
              {STAT_CARDS.map(({ key, label, icon: Icon, accent }) => (
                <div key={key} className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/5 rounded-xl sm:rounded-2xl p-3.5 sm:p-5" data-testid={`stat-${key}`}>
                  <div className="flex items-center justify-between mb-2 sm:mb-4">
                    <p className="font-manrope text-[10px] sm:text-xs text-paw-text-muted dark:text-white/40 uppercase tracking-wider">{label}</p>
                    <Icon className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${accent}`} strokeWidth={1.5} />
                  </div>
                  <p className="font-outfit font-bold text-2xl sm:text-3xl tracking-tight">{stats[key] ?? 0}</p>
                </div>
              ))}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
